import React from "react";
import { DateRangePicker } from "@mui/x-date-pickers-pro";
import { TextField, Box, Stack, Button, Typography } from "@mui/material";
import { useRecoilState } from "recoil";
import { adminAuditFilterAtom, historyFilterAtom } from "../../atoms/atoms";
import dayjs from "dayjs";

const AuditDateRangePicker = ({ original, data, setFilterDataset, type }) => {
  const [historyFilters, setHistoryFilters] = useRecoilState(historyFilterAtom);
  const [adminAudit, setAdminAudit] = useRecoilState(adminAuditFilterAtom);
  // Get the appropriate filter based on type
  const currentFilters = type === "admin" ? adminAudit : historyFilters;

  // Initialize dateRange from the appropriate filter state
  const [dateRange, setDateRange] = React.useState([
    currentFilters.start_date ? dayjs(currentFilters.start_date) : null,
    currentFilters.end_date ? dayjs(currentFilters.end_date) : null,
  ]);
  // Sync local state with Recoil state when it changes
  React.useEffect(() => {
    setDateRange([
      currentFilters.start_date ? dayjs(currentFilters.start_date) : null,
      currentFilters.end_date ? dayjs(currentFilters.end_date) : null,
    ]);
  }, [currentFilters.start_date, currentFilters.end_date]);

  const handleDateChange = (newValue) => {
    // Check if both dates are selected and valid
    if (newValue && newValue[0] && newValue[1]) {
      setDateRange(newValue);

      // Convert to ISO strings with proper time boundaries
      let fromDate = newValue[0].startOf("day").toISOString();
      let toDate = newValue[1].endOf("day").toISOString();

      if (type === "admin") {
        setAdminAudit((prev) => ({
          ...prev,
          start_date: fromDate,
          end_date: toDate,
          filtered: true,
        }));
      } else {
        setHistoryFilters((prev) => ({
          ...prev,
          start_date: fromDate,
          end_date: toDate,
          filtered: true,
        }));
      }

      // Filter the dataset if provided
      if (data && setFilterDataset) {
        let newData = data.filter(
          (d) =>
            new Date(d.auditDateCreated) >= new Date(fromDate) &&
            new Date(d.auditDateCreated) <= new Date(toDate)
        );
        setFilterDataset(newData);
      }
    }
  };

  const handleReset = () => {
    // If you want to reset to default 2-week range:
    const twoWeeksAgo = dayjs().subtract(2, "week").startOf("day");
    const today = dayjs().endOf("day");

    setDateRange([twoWeeksAgo, today]);

    if (type === "admin") {
      setAdminAudit((prev) => ({
        ...prev,
        auditor: "",
        pharmacy: "",
        auditType: "",
        outcome: "",
        start_date: twoWeeksAgo.toISOString(),
        end_date: today.toISOString(),
        filtered: true,
      }));
    } else {
      setHistoryFilters((prev) => ({
        ...prev,
        auditor: "",
        rx_id: "",
        auditType: "",
        outcome: "",
        start_date: twoWeeksAgo.toISOString(),
        end_date: today.toISOString(),
        filtered: true,
      }));
    }

    // Reset the dataset to original if provided
    if (original && setFilterDataset) {
      // Filter original data by the default date range
      const filteredData = original.filter(
        (d) =>
          new Date(d.auditDateCreated) >= twoWeeksAgo.toDate() &&
          new Date(d.auditDateCreated) <= today.toDate()
      );
      setFilterDataset(filteredData);
    }
  };

  return (
    <Stack sx={{ m: 1, display: "flex" }}>
      <Typography variant="overline">Filter by Audit Date</Typography>
      <Stack sx={{ m: 1, display: "flex" }} direction={"row"}>
        <DateRangePicker
          startText="Start Date"
          endText="End Date"
          value={dateRange}
          disabled={false}
          onChange={handleDateChange}
          renderInput={(startProps, endProps) => {
            return (
              <React.Fragment>
                <TextField
                  {...startProps}
                  sx={{ background: "whitesmoke" }}
                  variant="outlined"
                />
                <Box sx={{ mx: 1 }}> to </Box>
                <TextField
                  {...endProps}
                  sx={{ background: "whitesmoke" }}
                  variant="outlined"
                />
              </React.Fragment>
            );
          }}
        />
      </Stack>
      {type === "admin" && (
        <Button sx={{ m: 1 }} variant="contained" onClick={handleReset}>
          Reset All
        </Button>
      )}
    </Stack>
  );
};

export default AuditDateRangePicker;
